// @file The store for handling the surface onboarding demo padlet panel and its related logic
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { WallCreatedFromType } from '@@/enums'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { useSurfaceOnboardingDemoPadletAnimations } from '@@/vuecomposables/surface_onboarding_demo_padlet_animations'

import type { PostAttributes, Section } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

interface HeaderContent {
  title: string
  number?: number
  icon?: string
}

interface DemoPadletPanelVisibility {
  showSurfaceActionBar: boolean
  // Note: showSurfaceSearchBarButtons does not include the OzAvatar at the top of the SurfaceSearchBar
  showSurfaceActionBarButtons: boolean
  showSurfaceAddPost: boolean
  showSurfaceSearchBar: boolean
  showModifiedSurfaceAddPostButton: boolean
  showDialogPermitNotification: boolean
}

export enum DemoPadletPanelStep {
  Landing = 'landing',
  Topic = 'topic',
  Post = 'post',
  Share = 'share',
  Done = 'done',
}

export enum StepStatus {
  NotStarted = 'notstarted',
  InProgress = 'inprogress',
  Complete = 'complete',
}

export const LANDING_PANEL_STEPS = [
  { text: __('Set a topic') },
  { text: __('Add some posts') },
  { text: __('Share and collaborate') },
]

function getPostDataWithSections(sections: Section[]): PostAttributes[] {
  return [
    {
      subject: __('Golden Gate Bridge'),
      body: __('San Francisco, CA'),
      attachment:
        'https://images.unsplash.com/photo-1521747116042-5a810fda9664?q=80&w=2970&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attachment_caption: __('Location'),
      wish_content: {
        attachment_props: {
          url: 'https://images.unsplash.com/photo-1521747116042-5a810fda9664?q=80&w=2970&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
        },
      },
      wallSectionId: sections[0]?.id,
      wall_section_hashid: sections[0]?.hashid,
    },
    {
      subject: __('Disneyland'),
      body: __('Daisy here! 🌼 Just had a blast at Disneyland. Met so many friends – real and costumed! 🎡'),
      attachment:
        'https://images.unsplash.com/photo-1626816205347-91b13437b39a?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGRpc25leWxhbmR8ZW58MHwwfDB8fHwy',
      attachment_caption: __('Location'),
      wish_content: {
        attachment_props: {
          url: 'https://images.unsplash.com/photo-1521747116042-5a810fda9664?q=80&w=2970&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
        },
      },
      wallSectionId: sections[1]?.id,
      wall_section_hashid: sections[1]?.hashid,
    },
    {
      subject: __('Wonderland'),
      body: __(
        "Ever since I left, I've been longing to tumble back down the rabbit hole to revisit Wonderland. I miss the enigmatic Cheshire Cat's riddles, the whimsical tea parties with the Mad Hatter, and the Queen's eccentric croquet games. A trip back to that fantastical realm would be a dream come true!",
      ),
      attachment:
        'https://images.unsplash.com/photo-1621461526336-cfe08596d398?q=80&w=1632&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attachment_caption: __('Location'),
      wish_content: {
        attachment_props: {
          url: 'https://images.unsplash.com/photo-1521747116042-5a810fda9664?q=80&w=2970&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
        },
      },
      wallSectionId: sections[1]?.id,
      wall_section_hashid: sections[1]?.hashid,
    },
    {
      subject: __('Bermuda Triange'),
      body: __(
        'The Bermuda Triangle? A mystery even I prefer to steer clear of. Too many unsolved disappearances for my taste!',
      ),
      attachment:
        'https://images.unsplash.com/photo-1627408413283-83bd1c42de2d?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8YmVybXVkYSUyMHRyaWFuZ2xlfGVufDB8MHwwfHx8Mg%3D%3D',
      attachment_caption: __('Western North Atlantic Ocean'),
      wish_content: {
        attachment_props: {
          url: 'https://images.unsplash.com/photo-1521747116042-5a810fda9664?q=80&w=2970&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
        },
      },
      wallSectionId: sections[2]?.id,
      wall_section_hashid: sections[2]?.hashid,
    },
  ]
}
export const useSurfaceOnboardingDemoPadletPanelStore = defineStore('surfaceOnboardingDemoPadletPanelStore', () => {
  const windowSizeStore = useWindowSizeStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const postComposerModalStore = usePostComposerModalStore()
  const surfacePostsStore = useSurfacePostsStore()

  // ------------------------------
  // General
  // ------------------------------

  const isDemoPadletPanel = computed(() => {
    return (
      isAppUsing('demoPadletPanel') &&
      surfaceStartingStateStore.startingState.onboardingFlow?.wallCreatedFromType === WallCreatedFromType.demo
    )
  })
  const isDemoPadletPanelMobile = computed(() => {
    return isDemoPadletPanel.value && !windowSizeStore.isBiggerThanTabletPortrait
  })
  const isDemoPadletPanelDesktop = computed(() => {
    // Remember this should evaluate to false when demo padlet onboarding is completed
    return isDemoPadletPanel.value && windowSizeStore.isBiggerThanTabletPortrait
  })

  // The activeStep controls which step panel that is displayed, the activeStepStatus tracks the progress of the active step
  const activeStep = ref(DemoPadletPanelStep.Landing)
  const activeStepStatus = ref(StepStatus.NotStarted)
  function setActiveStepStatus(status: StepStatus): void {
    activeStepStatus.value = status
  }

  const isStepNotStarted = computed(() => {
    return activeStepStatus.value === StepStatus.NotStarted
  })
  const isStepInProgress = computed(() => {
    return activeStepStatus.value === StepStatus.InProgress
  })
  const isStepCompleted = computed(() => {
    return activeStepStatus.value === StepStatus.Complete
  })

  function goToNextStep(): void {
    switch (activeStep.value) {
      case DemoPadletPanelStep.Landing:
        activeStep.value = DemoPadletPanelStep.Topic
        break
      case DemoPadletPanelStep.Topic:
        activeStep.value = DemoPadletPanelStep.Post
        break
      case DemoPadletPanelStep.Post:
        activeStep.value = DemoPadletPanelStep.Share
        break
      case DemoPadletPanelStep.Share:
        activeStep.value = DemoPadletPanelStep.Done
        break
      default:
        activeStep.value = DemoPadletPanelStep.Landing
    }
    // Every time we change steps we reset progress
    setActiveStepStatus(StepStatus.NotStarted)
  }

  const currentHeader = computed(() => getHeaderContent(activeStep.value))
  function getHeaderContent(step): HeaderContent {
    switch (step) {
      case DemoPadletPanelStep.Topic:
        return {
          title: __('Set a topic'),
          number: 1,
        }
      case DemoPadletPanelStep.Post:
        return {
          title: __('Post'),
          number: 2,
        }
      case DemoPadletPanelStep.Share:
        return {
          title: __('Share and collaborate'),
          number: 3,
        }
      case DemoPadletPanelStep.Done:
        return {
          title: __('Congratulations'),
          icon: 'firework_explosion',
        }
      default:
        return {
          title: '',
          number: 0,
        }
    }
  }

  const sectionNames = [__('Favorite places'), __('Wishlist destinations'), __('No-go zones')]
  const postData = computed<PostAttributes[]>(() => {
    const sections = useSurfaceSectionsStore().sortedSections
    return getPostDataWithSections(sections)
  })

  // ------------------------------
  // Hiding UI
  // ------------------------------

  const demoPadletPanelVisibility = computed<DemoPadletPanelVisibility>(() => {
    // If the demo padlet panel is not desktop, we don't need to hide any UI elements so we show everything
    if (!isDemoPadletPanelDesktop.value) {
      return {
        showSurfaceActionBar: true,
        showSurfaceActionBarButtons: true,
        showSurfaceAddPost: true,
        showSurfaceSearchBar: true,
        showSurfaceSearchBarButtons: true,
        showModifiedSurfaceAddPostButton: false,
        showDialogPermitNotification: true,
      }
    }
    // Note when adding a new piece of UI, every step previous to the step it's shown should be hidden
    switch (activeStep.value) {
      case DemoPadletPanelStep.Landing:
        return {
          showSurfaceActionBar: false,
          showSurfaceActionBarButtons: false,
          showSurfaceAddPost: false,
          showSurfaceSearchBar: false,
          showModifiedSurfaceAddPostButton: false,
          showDialogPermitNotification: false,
        }
      case DemoPadletPanelStep.Topic:
        return {
          showSurfaceActionBar: false,
          showSurfaceActionBarButtons: false,
          showSurfaceAddPost: false,
          showSurfaceSearchBar: false,
          showModifiedSurfaceAddPostButton: false,
          showDialogPermitNotification: false,
        }
      case DemoPadletPanelStep.Post:
        return {
          showSurfaceActionBar: true,
          showSurfaceActionBarButtons: false,
          showSurfaceAddPost: !hasAddedPost.value,
          showSurfaceSearchBar: false,
          showModifiedSurfaceAddPostButton: true,
          showDialogPermitNotification: true,
        }
      default:
        // By default we show everything
        return {
          showSurfaceActionBar: true,
          showSurfaceActionBarButtons: true,
          showSurfaceAddPost: true,
          showSurfaceSearchBar: true,
          // By default we don't show the modified SurfaceAddPostButton
          showModifiedSurfaceAddPostButton: false,
          showDialogPermitNotification: true,
        }
    }
  })

  // ------------------------------
  // Landing step
  // ------------------------------

  const isLandingStep = computed(() => {
    return (
      // TODO: ENG-15161 Refactor to remove this temporary check against activeStep and only read values from surfaceStartingStateStore (this will be after the upserter is implemented)
      // using this line: surfaceStartingStateStore.startingState.onboardingFlow?.wallCreatedFromType === WallCreatedFromType.demo
      activeStep.value === DemoPadletPanelStep.Landing
    )
  })

  // ------------------------------
  // Topic step
  // ------------------------------

  // ------------------------------
  // Post step
  // ------------------------------

  const isPostStep = computed(() => {
    return activeStep.value === DemoPadletPanelStep.Post
  })

  const hasAddedPost = computed(() => {
    return surfacePostsStore.currentPostsCount > 0
  })

  // ------------------------------
  // Share step
  // ------------------------------
  const {
    isSendingOutLinksCompleted,
    isMakingContributionsCompleted,
    isCollaboratingAndInteractingCompleted,
    startShareAnimation,
  } = useSurfaceOnboardingDemoPadletAnimations(activeStep.value)

  const isShareStep = computed(() => {
    return activeStep.value === DemoPadletPanelStep.Share
  })

  const handleStartShareAnimation = (): void => {
    startShareAnimation()
  }

  watch(
    () => postComposerModalStore.hasExpandedComposerModal,
    (hasExpandedComposerModal: boolean) => {
      if (hasExpandedComposerModal && !isStepInProgress.value) {
        setActiveStepStatus(StepStatus.InProgress)
      }
    },
  )

  return {
    // ------------------------------
    // General
    // ------------------------------
    isDemoPadletPanel,
    isDemoPadletPanelMobile,
    isDemoPadletPanelDesktop,
    activeStep,
    activeStepStatus,
    setActiveStepStatus,
    isStepNotStarted,
    isStepInProgress,
    isStepCompleted,
    goToNextStep,
    getHeaderContent,
    currentHeader,
    sectionNames,
    postData,
    // ------------------------------
    // Hiding UI
    // ------------------------------
    demoPadletPanelVisibility,
    // ------------------------------
    // Landing step
    // ------------------------------
    isLandingStep,
    // ------------------------------
    // Topic step
    // ------------------------------
    // ------------------------------
    // Post step
    // ------------------------------
    isPostStep,
    hasAddedPost,
    // ------------------------------
    // Share step
    // ------------------------------
    isShareStep,
    isSendingOutLinksCompleted,
    isMakingContributionsCompleted,
    isCollaboratingAndInteractingCompleted,
    handleStartShareAnimation,
  }
})
